import React, { useEffect, useState } from "react";
import {
    LineChartOutlined,
    SaveOutlined,
    DeleteOutlined,
    ArrowLeftOutlined
} from "@ant-design/icons";

import {
    canSyncHealthData,
    getHealthHistory,
    saveHealthData,
    deleteHealthData
} from "../../api/healthTracking";

export default function NhipTim() {
    const [heartRate, setHeartRate] = useState("");
    const [status, setStatus] = useState("nghi-ngoi");
    const [note, setNote] = useState("");
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const syncWithAccount = canSyncHealthData();

    const getStatusLabel = (value) => {
        switch (value) {
            case "nghi-ngoi":
                return "Khi nghỉ ngơi";

            case "sau-van-dong":
                return "Sau vận động";

            case "sau-tap":
                return "Sau tập luyện";

            case "truoc-ngu":
                return "Trước khi ngủ";

            case "sau-ngu":
                return "Sau khi ngủ dậy";

            default:
                return "Khác";
        }
    };

    useEffect(() => {
        const loadHistory = async () => {
            setLoading(true);

            if (syncWithAccount) {
                const result = await getHealthHistory("nhip_tim");

                if (result.success) {
                    const data = result.data.map((item) => ({
                        id: item.health_tracking_id,
                        heartRate: item.tracking_data?.heartRate,
                        status: item.tracking_data?.status,
                        statusLabel: item.tracking_data?.statusLabel,
                        note: item.tracking_data?.note,
                        date: item.tracking_data?.date,
                        time: item.tracking_data?.time,
                        database: true
                    }));

                    setHistory(data);
                } else {
                    setHistory([]);
                }
            } else {
                const savedData =
                    localStorage.getItem("nhipTimHistory");

                if (savedData) {
                    try {
                        setHistory(JSON.parse(savedData));
                    } catch (error) {
                        setHistory([]);
                    }
                } else {
                    setHistory([]);
                }
            }

            setLoading(false);
        };

        loadHistory();
    }, [syncWithAccount]);

    const reloadAccountHistory = async () => {
        const result = await getHealthHistory("nhip_tim");

        if (!result.success) {
            return false;
        }

        const data = result.data.map((item) => ({
            id: item.health_tracking_id,
            heartRate: item.tracking_data?.heartRate,
            status: item.tracking_data?.status,
            statusLabel: item.tracking_data?.statusLabel,
            note: item.tracking_data?.note,
            date: item.tracking_data?.date,
            time: item.tracking_data?.time,
            database: true
        }));

        setHistory(data);

        return true;
    };

    const handleSave = async () => {
        if (!heartRate) {
            alert("Vui lòng nhập nhịp tim.");
            return;
        }

        const heartRateNumber = Number(heartRate);

        if (heartRateNumber <= 0) {
            alert("Vui lòng nhập chỉ số hợp lệ.");
            return;
        }

        const now = new Date();

        const newRecord = {
            id: Date.now(),
            heartRate: heartRateNumber,
            status,
            statusLabel: getStatusLabel(status),
            note: note.trim(),
            date: now.toLocaleDateString("vi-VN"),
            time: now.toLocaleTimeString("vi-VN", {
                hour: "2-digit",
                minute: "2-digit"
            })
        };

        if (syncWithAccount) {
            setSaving(true);

            const result = await saveHealthData(
                "nhip_tim",
                {
                    heartRate: newRecord.heartRate,
                    status: newRecord.status,
                    statusLabel: newRecord.statusLabel,
                    note: newRecord.note,
                    date: newRecord.date,
                    time: newRecord.time
                }
            );

            if (!result.success) {
                setSaving(false);

                alert(
                    "Không thể lưu dữ liệu vào tài khoản. Vui lòng thử lại."
                );

                return;
            }

            await reloadAccountHistory();

            setSaving(false);
        } else {
            const newHistory = [
                newRecord,
                ...history
            ];

            setHistory(newHistory);

            localStorage.setItem(
                "nhipTimHistory",
                JSON.stringify(newHistory)
            );
        }

        setHeartRate("");
        setStatus("nghi-ngoi");
        setNote("");
    };

    const handleDelete = async (id) => {
        if (syncWithAccount) {
            const result = await deleteHealthData(id);

            if (!result.success) {
                alert(
                    "Không thể xóa dữ liệu. Vui lòng thử lại."
                );

                return;
            }

            await reloadAccountHistory();
        } else {
            const newHistory = history.filter(
                (item) => item.id !== id
            );

            setHistory(newHistory);

            localStorage.setItem(
                "nhipTimHistory",
                JSON.stringify(newHistory)
            );
        }
    };

    return (
        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">

            {/* QUAY LẠI */}
            <a
                href="/tienich/suc-khoe"
                className="inline-flex items-center text-blue-600 font-semibold text-sm mb-4"
            >
                <ArrowLeftOutlined className="mr-2" />
                Theo dõi sức khỏe
            </a>


            {/* PHẦN ĐẦU */}
            <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">
                        <LineChartOutlined />
                    </div>

                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">
                            THEO DÕI SỨC KHỎE
                        </p>

                        <h1 className="text-[25px] md:text-3xl font-bold">
                            Nhịp tim
                        </h1>

                    </div>

                </div>

                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal">
                    Ghi lại nhịp tim theo từng thời điểm để theo dõi sự thay đổi hằng ngày.
                </p>

            </div>


            {/* TRẠNG THÁI */}
            {syncWithAccount ? (

                <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 mt-4">

                    <p className="text-[13px] text-green-700 font-semibold">
                        ✓ Dữ liệu đang được lưu theo tài khoản
                    </p>

                    <p className="text-[12px] text-green-600 mt-1">
                        Bạn có thể đăng nhập tài khoản này trên thiết bị khác để xem lại lịch sử.
                    </p>

                </div>

            ) : (

                <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3 mt-4">

                    <p className="text-[13px] text-yellow-700 font-semibold">
                        Dữ liệu đang được lưu trên thiết bị này
                    </p>

                    <p className="text-[12px] text-yellow-600 mt-1">
                        Đăng nhập bằng tài khoản có mật khẩu để lưu lịch sử theo tài khoản.
                    </p>

                </div>

            )}


            {/* FORM */}
            <div className="bg-white border rounded-2xl p-4 md:p-6 mt-5">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Nhập chỉ số
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                    Nhập nhịp tim hiện tại của bạn.
                </p>


                <div className="mt-5">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nhịp tim
                    </label>

                    <div className="relative">

                        <input
                            type="number"
                            value={heartRate}
                            onChange={(e) =>
                                setHeartRate(e.target.value)
                            }
                            placeholder="Ví dụ: 72"
                            className="w-full h-12 border rounded-xl px-3 pr-24 outline-none focus:border-blue-500"
                        />

                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                            lần/phút
                        </span>

                    </div>

                </div>


                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Thời điểm đo
                    </label>

                    <select
                        value={status}
                        onChange={(e) =>
                            setStatus(e.target.value)
                        }
                        className="w-full h-12 border rounded-xl px-3 bg-white outline-none focus:border-blue-500"
                    >

                        <option value="nghi-ngoi">
                            Khi nghỉ ngơi
                        </option>

                        <option value="sau-van-dong">
                            Sau vận động
                        </option>

                        <option value="sau-tap">
                            Sau tập luyện
                        </option>

                        <option value="truoc-ngu">
                            Trước khi ngủ
                        </option>

                        <option value="sau-ngu">
                            Sau khi ngủ dậy
                        </option>

                        <option value="khac">
                            Khác
                        </option>

                    </select>

                </div>


                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Ghi chú
                        <span className="font-normal text-gray-400 ml-1">
                            (không bắt buộc)
                        </span>
                    </label>

                    <textarea
                        value={note}
                        onChange={(e) =>
                            setNote(e.target.value)
                        }
                        placeholder="Ví dụ: vừa đi bộ 15 phút..."
                        rows={3}
                        className="w-full border rounded-xl px-3 py-3 outline-none focus:border-blue-500 resize-none"
                    />

                </div>


                <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`w-full mt-5 h-12 rounded-xl text-white font-bold ${
                        saving
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                    }`}
                >

                    <SaveOutlined className="mr-2" />

                    {saving
                        ? "Đang lưu..."
                        : "Lưu chỉ số"}

                </button>

            </div>


            {/* LỊCH SỬ */}
            <div className="mt-7">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Lịch sử gần đây
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                    {syncWithAccount
                        ? "Các chỉ số được lưu theo tài khoản của bạn."
                        : "Các chỉ số bạn đã lưu trên thiết bị này."}
                </p>


                {loading ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <p className="text-sm text-gray-500">
                            Đang tải dữ liệu...
                        </p>

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <LineChartOutlined className="text-3xl text-gray-300" />

                        <p className="font-semibold text-gray-700 mt-3">
                            Chưa có dữ liệu
                        </p>

                        <p className="text-sm text-gray-500 mt-1">
                            Nhịp tim bạn lưu sẽ xuất hiện tại đây.
                        </p>

                    </div>

                ) : (

                    <div className="space-y-3 mt-4">

                        {history.map((item) => (

                            <div
                                key={item.id}
                                className="bg-white border rounded-2xl p-4"
                            >

                                <div className="flex items-start justify-between">

                                    <div>

                                        <div className="flex items-end">

                                            <p className="text-2xl font-bold text-gray-900">
                                                {item.heartRate}
                                            </p>

                                            <span className="text-xs text-gray-400 ml-2 mb-1">
                                                lần/phút
                                            </span>

                                        </div>

                                        <p className="text-sm text-pink-600 font-medium mt-1">
                                            {item.statusLabel}
                                        </p>

                                        <p className="text-xs text-gray-400 mt-1">
                                            {item.time} - {item.date}
                                        </p>

                                    </div>


                                    <button
                                        onClick={() =>
                                            handleDelete(item.id)
                                        }
                                        className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center"
                                        aria-label="Xóa chỉ số"
                                    >
                                        <DeleteOutlined />
                                    </button>

                                </div>


                                {item.note && (

                                    <div className="bg-gray-50 rounded-xl px-3 py-2 mt-3">

                                        <p className="text-xs text-gray-500">
                                            {item.note}
                                        </p>

                                    </div>

                                )}

                            </div>

                        ))}

                    </div>

                )}

            </div>


            {/* LƯU Ý */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-7">

                <p className="font-semibold text-gray-800 text-sm">
                    Lưu ý
                </p>

                {syncWithAccount ? (

                    <p className="text-[13px] text-gray-600 leading-5 mt-1">
                        Dữ liệu nhịp tim được lưu theo tài khoản đang đăng nhập và có thể được tải lại trên thiết bị khác khi sử dụng cùng tài khoản.
                    </p>

                ) : (

                    <p className="text-[13px] text-gray-600 leading-5 mt-1">
                        Dữ liệu hiện được lưu trên trình duyệt của thiết bị bạn đang sử dụng.
                        Nếu xóa dữ liệu trình duyệt hoặc đổi thiết bị, lịch sử có thể không còn.
                    </p>

                )}

                <p className="text-[13px] text-gray-500 leading-5 mt-2">
                    Tiện ích này chỉ hỗ trợ ghi lại và theo dõi nhịp tim theo thời gian,
                    không dùng để tự chẩn đoán hoặc thay thế đánh giá y tế.
                </p>

            </div>

        </div>
    );
}